import React, { useState } from "react";
import { View, StyleSheet, TouchableOpacity } from "react-native";
import { CaretDownIcon, CaretRightIcon } from "@phosphor-icons/react";

import { ActivityAnalytics, ModuleAnalytics } from "../../../../graphql/generated/graphql";
import { formatTime, formatPercentage, getSentimentColor } from "../../../../utils/analyticsUtils";
import CustomText from "../../../common/general/CustomText/CustomText";
import CustomSpacing from "../../../common/layout/CustomSpacing/CustomSpacing";
import ActivityPanel from "./ActivityPanel";
import ProgressBar from "./ProgressBar";
import StatBadge from "./StatBadge";

const hasEngagement = (item: ModuleAnalytics | ActivityAnalytics): boolean => {
  return (
    (item.success_points && item.success_points > 0) ||
    (item.confusion_points && item.confusion_points > 0) ||
    (item.questions_asked && item.questions_asked > 0) ||
    (item.active_interaction_count && item.active_interaction_count > 0) ||
    (item.distractions && item.distractions > 0)
  );
};

const sortByEngagement = (items: ModuleAnalytics[] | ActivityAnalytics[]): ModuleAnalytics[] | ActivityAnalytics[] => {
  // @ts-expect-error - _typename is different for ModuleAnalytics and ActivityAnalytics
  return [...items].sort((a, b) => {
    const aActive = hasEngagement(a);
    const bActive = hasEngagement(b);
    if (aActive === bActive) return 0;
    return aActive ? -1 : 1;
  });
};

interface ModulePanelProps {
  module?: ModuleAnalytics;
  index?: number;
  inactive?: boolean;
  summaryStats?: React.ReactNode;
  loading?: boolean;
}

const ModulePanel: React.FC<ModulePanelProps> = ({
  module,
  index,
  inactive = false,
  summaryStats,
  loading = false,
}) => {
  const [expanded, setExpanded] = useState(false);

  // Format completion percentage correctly
  const formatCompletionPercentage = (value: number | undefined): string => {
    if (value === undefined || value === null) return formatPercentage(0);
    // If value is already in percentage form (> 1), don't multiply by 100
    return value > 1 ? formatPercentage(value) : formatPercentage(value * 100);
  };

  // Calculate completion for progress bar
  // TODO: This data point is not available. We need to get it from the backend. Right now completion_percentage is only calculate for objectives. that needs to be consolidated upstream to activities and then to modules.
  // const completionPercentage = module?.completion_percentage
  //   ? module.completion_percentage > 1
  //     ? module.completion_percentage
  //     : module.completion_percentage * 100
  //   : 0;
  const completionPercentage = 0;

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <CustomText text="Loading module..." />
      </View>
    );
  }

  return (
    <View style={[styles.container, { opacity: inactive ? 0.6 : 1 }]}>
      <TouchableOpacity style={styles.header} onPress={() => !inactive && setExpanded(!expanded)} disabled={inactive}>
        <View style={styles.headerLeft}>
          {expanded ? (
            <CaretDownIcon size={16} color="#6B7280" style={styles.expandIcon} />
          ) : (
            <CaretRightIcon size={16} color="#6B7280" style={styles.expandIcon} />
          )}
          <CustomText text={module?.label || `Module ${(index ?? 0) + 1}`} weight="bold" useTranslationText={false} />
          {module?.sentiment && (
            <View
              style={[
                styles.sentimentBadge,
                {
                  backgroundColor: getSentimentColor(module.sentiment) + "20",
                  borderColor: getSentimentColor(module.sentiment),
                },
              ]}>
              <CustomText
                text={module.sentiment}
                size="xs"
                style={{ color: getSentimentColor(module.sentiment), textTransform: "capitalize" }}
                useTranslationText={false}
              />
            </View>
          )}
        </View>
      </TouchableOpacity>

      {/* Always show progress bar and key metrics */}
      {completionPercentage > 0 && (
        <View style={styles.progressSection}>
          <View style={styles.progressHeader}>
            <CustomText text="Completion" size="xs" />
            <CustomText text={formatCompletionPercentage(completionPercentage)} size="xs" useTranslationText={false} />
          </View>
          <ProgressBar percentage={completionPercentage} size="sm" />
        </View>
      )}

      <View style={styles.metricsRow}>
        <StatBadge label="Success" value={module?.success_points || 0} color="#10B981" />
        <StatBadge label="Confusion" value={module?.confusion_points || 0} color="#EF4444" />
        <StatBadge label="Questions" value={module?.questions_asked || 0} color="#3B82F6" />
        <StatBadge label="Interactions" value={module?.active_interaction_count || 0} color="#8B5CF6" />
        {/* <StatBadge label="Time" value={formatTime(module?.total_time_taken || 0)} color="#6B7280" /> */}
      </View>

      {/* Summary stats if provided */}
      {summaryStats && <View style={styles.summaryStatsContainer}>{summaryStats}</View>}

      {expanded && (
        <View style={styles.expandedContent}>
          <CustomSpacing type="vertical" size="m" />

          <View style={styles.detailsSection}>
            <CustomText text="Module Details" weight="bold" />
            <CustomSpacing type="vertical" size="s" />

            <View style={styles.detailsGrid}>
              <View style={styles.detailItem}>
                <CustomText text="Success Points" size="xs" style={styles.detailLabel} />
                <CustomText text={`${module?.success_points || 0}`} size="l" weight="bold" useTranslationText={false} />
              </View>
              <View style={styles.detailItem}>
                <CustomText text="Confusion Points" size="xs" style={styles.detailLabel} />
                <CustomText
                  text={`${module?.confusion_points || 0}`}
                  size="l"
                  weight="bold"
                  useTranslationText={false}
                />
              </View>
              <View style={styles.detailItem}>
                <CustomText text="Questions Asked" size="xs" style={styles.detailLabel} />
                <CustomText
                  text={`${module?.questions_asked || 0}`}
                  size="l"
                  weight="bold"
                  useTranslationText={false}
                />
              </View>
              <View style={styles.detailItem}>
                <CustomText text="Avg. Activity Time" size="xs" style={styles.detailLabel} />
                <CustomText
                  text={formatTime(module?.average_time_taken || 0)}
                  size="l"
                  weight="bold"
                  useTranslationText={false}
                />
              </View>
            </View>
          </View>

          <CustomSpacing type="vertical" size="l" />

          <View style={styles.activitiesSection}>
            <CustomText text="Activities" weight="bold" style={styles.activitiesTitle} />
            <CustomSpacing type="vertical" size="s" />

            {module?.details?.activities && module.details.activities.length > 0 ? (
              <View>
                {sortByEngagement(module.details.activities).map((activity, idx) => (
                  <ActivityPanel
                    key={activity.id || idx}
                    activity={activity}
                    index={idx}
                    inactive={!hasEngagement(activity)}
                  />
                ))}
              </View>
            ) : (
              <CustomText text="No activities in this module." size="s" style={styles.noDataText} />
            )}
          </View>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    backgroundColor: "#FFFFFF",
    padding: 12,
  },
  loadingContainer: {
    padding: 16,
    alignItems: "center",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  expandIcon: {
    marginRight: 8,
  },
  sentimentBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 12,
    borderWidth: 1,
    marginLeft: 8,
  },
  progressSection: {
    marginTop: 8,
  },
  progressHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  metricsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
    marginTop: 8,
  },
  summaryStatsContainer: {
    marginTop: 8,
  },
  expandedContent: {
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
    paddingTop: 12,
    marginTop: 12,
  },
  detailsSection: {
    marginBottom: 16,
  },
  detailsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
  },
  detailItem: {
    minWidth: 120,
    flex: 1,
    alignItems: "center",
    gap: 8,
  },
  detailLabel: {
    color: "#6B7280",
  },
  activitiesSection: {
    marginBottom: 16,
  },
  activitiesTitle: {
    color: "#3B82F6",
  },
  noDataText: {
    color: "#6B7280",
    fontStyle: "italic",
    paddingVertical: 8,
  },
});

export default ModulePanel;
